import { cleanPermalink } from './top-videos';
import { format } from 'date-fns';

import { fetchImageForDocx } from './docx-image';
import type { ReportParams, ReportRow } from './export-account-report-sheets';
import { downloadDocx, type DocxFile } from './open-docx-in-gdocs';

// Отчёт министру: без таблицы. Каждая публикация — блок строк (№+Тема, дата,
// ссылка, просмотры, скрин). Сортировка старая→новая дата. Публикации с
// просмотрами < LOW_VIEWS уходят отдельным списком в конце, нумерация сквозная.
const LOW_VIEWS = 5000;

// Рамка для скрина в px, сохраняя пропорции исходника (обычно 9:16).
const IMAGE_BOX = { width: 124, height: 220 };

export async function buildAccountReportDocx(params: ReportParams): Promise<DocxFile> {
  if (params.rows.length === 0) throw new Error('Нет данных для экспорта');

  const title = `${params.accountTitle} ${params.period}`.trim();

  // Старая → новая дата. Сквозная нумерация: сначала «нормальные», потом «низкие».
  // Без даты (ручные строки отчёта по ссылкам) — в конец, иначе NaN ломает сортировку.
  const time = (r: ReportRow) => {
    const t = r.publishedAt ? new Date(r.publishedAt).getTime() : NaN;
    return Number.isFinite(t) ? t : Infinity;
  };
  const sorted = [...params.rows].sort((a, b) => {
    const ta = time(a);
    const tb = time(b);
    return ta === tb ? 0 : ta < tb ? -1 : 1;
  });
  const high = sorted.filter((r) => r.views >= LOW_VIEWS);
  const low = sorted.filter((r) => r.views < LOW_VIEWS);

  // Скрины тянем только для «нормальных» — у «низких» только ссылка.
  const images = await Promise.all(
    high.map((row) => (row.imageUrl ? fetchImageForDocx(row.imageUrl, IMAGE_BOX) : Promise.resolve(null))),
  );

  const docx = await import('docx');
  const {
    Document,
    Packer,
    Paragraph,
    TextRun,
    ExternalHyperlink,
    ImageRun,
    AlignmentType,
    HeadingLevel,
  } = docx;

  const linkRun = (url: string) =>
    new ExternalHyperlink({
      link: url,
      children: [new TextRun({ text: url, style: 'Hyperlink' })],
    });

  // Блок одной «нормальной» публикации: №+Тема (жирный заголовок), дата, ссылка,
  // просмотры, скрин, пустая строка-разделитель.
  const highBlocks = high.flatMap((row: ReportRow, i) => {
    const n = i + 1;
    const heading = [String(n), row.theme].filter(Boolean).join('. ');
    const date = row.publishedAt ? format(new Date(row.publishedAt), 'dd.MM.yyyy') : '';
    const img = images[i];

    return [
      new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [new TextRun({ text: heading, bold: true, color: '000000' })],
      }),
      new Paragraph({ children: [new TextRun({ text: date })] }),
      new Paragraph({ children: [linkRun(cleanPermalink(row.permalink))] }),
      new Paragraph({ children: [new TextRun({ text: `Просмотры: ${row.views.toLocaleString('ru-RU')}` })] }),
      img
        ? new Paragraph({
            children: [
              new ImageRun({
                data: img.data,
                type: img.type,
                transformation: { width: img.width, height: img.height },
              }),
            ],
          })
        : new Paragraph({ children: [] }),
      new Paragraph({ children: [] }),
    ];
  });

  // Список «низких»: продолжаем сквозную нумерацию, только №+ссылка.
  const lowBlocks =
    low.length > 0
      ? [
          new Paragraph({
            heading: HeadingLevel.HEADING_3,
            children: [new TextRun({ text: 'Низкие просмотры (менее 5000 просмотров)', bold: true, color: '000000' })],
          }),
          ...low.map(
            (row, i) =>
              new Paragraph({
                children: [new TextRun({ text: `${high.length + i + 1}. ` }), linkRun(cleanPermalink(row.permalink))],
              }),
          ),
        ]
      : [];

  const doc = new Document({
    sections: [
      {
        children: [
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            alignment: AlignmentType.LEFT,
            children: [new TextRun({ text: title, color: '000000' })],
          }),
          ...highBlocks,
          ...lowBlocks,
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const filename = title.replace(/[\\/:*?"<>|]/g, '_') || 'report';
  return { blob, filename: `${filename}.docx` };
}

export async function generateAccountReportDocx(params: ReportParams): Promise<void> {
  downloadDocx(await buildAccountReportDocx(params));
}
